import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../App';
import { appId } from '../config/firebaseConfig';
import DOMPurify from 'dompurify';
import TechBackground from '../components/TechBackground';

export default function PublicTechnicalReportPage() {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pegar o ID da URL (?public=laudo&reportId=...)
    const urlParams = new URLSearchParams(window.location.search);
    const reportId = urlParams.get('reportId');

    useEffect(() => {
        const fetchReport = async () => {
            if (!reportId) {
                setError("ID do laudo não fornecido.");
                setLoading(false);
                return;
            }

            try {
                const docRef = doc(db, `/artifacts/${appId}/public/data/technical_reports`, reportId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setReport({ id: docSnap.id, ...docSnap.data() });
                } else {
                    setError("Laudo não encontrado ou removido.");
                }
            } catch (err) {
                console.error("Erro ao buscar laudo:", err);
                setError("Ocorreu um erro ao carregar o documento. Verifique sua conexão.");
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
    }, [reportId]);

    const renderRichText = (html) => {
        if (!html) return { __html: '' };
        return { __html: DOMPurify.sanitize(html) };
    };

    const ensureUrl = (url) => {
        if (!url) return '';
        const trimmed = url.trim();
        if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
            return `https://${trimmed}`;
        }
        return trimmed;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950 text-cyan-400 font-black animate-pulse uppercase tracking-[0.3em] text-xs">
                Autenticando documento Arcus...
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950 p-8">
                <div className="max-w-md w-full bg-red-500/10 border border-red-500/20 p-8 rounded-2xl text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    <h2 className="text-xl font-black text-white uppercase tracking-tighter mb-2">Erro de Acesso</h2>
                    <p className="text-slate-400 text-sm leading-relaxed mb-6">{error}</p>
                    <button onClick={() => window.location.reload()} className="bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-xl font-bold transition-all text-xs uppercase tracking-widest">Tentar Novamente</button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 font-sans text-slate-900 relative">
            <TechBackground />
            
            {/* Overlay de Desfoque para legibilidade */}
            <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-[2px] z-0"></div>

            <style>
                {`
                .report-container {
                    background: white;
                    width: 210mm;
                    max-width: 95%;
                    margin: 40px auto;
                    min-height: 297mm;
                    padding: 20mm;
                    box-shadow: 0 40px 100px -20px rgba(0,0,0,0.5);
                    position: relative;
                    z-index: 1;
                }
                
                /* Marca d'água (Garante repetição em todas as páginas no PDF) */
                .watermark-fixed {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 500px;
                    height: 500px;
                    background-image: url('/logo.png');
                    background-repeat: no-repeat;
                    background-position: center;
                    background-size: contain;
                    opacity: 0.10;
                    pointer-events: none;
                    z-index: -1;
                }

                @media print {
                    .watermark-fixed {
                        position: fixed; /* O segrego para repetir em cada página */
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                    }
                }
                .report-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-bottom: 4px solid #000;
                    padding-bottom: 25px;
                    margin-bottom: 35px;
                }
                
                .header-logo {
                    height: 65px;
                    object-fit: contain;
                    opacity: 1 !important;
                    display: block;
                }
                .report-title-box { text-align: right; }
                .report-title { 
                    font-size: 24pt; 
                    font-weight: 900; 
                    margin: 0; 
                    text-transform: uppercase; 
                    letter-spacing: -1px;
                    line-height: 1;
                }
                .report-subtitle { 
                    font-size: 10pt; 
                    color: #555; 
                    margin: 5px 0 0 0; 
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 2px;
                }
                .report-section { margin-bottom: 30px; }
                .section-label { 
                    font-size: 8.5pt; 
                    font-weight: 950; 
                    text-transform: uppercase; 
                    color: #000; 
                    letter-spacing: 2px;
                    margin-bottom: 8px;
                    border-bottom: 1px solid #eee;
                    padding-bottom: 4px;
                    white-space: nowrap;
                }
                .section-content { 
                    font-size: 11pt; 
                    line-height: 1.6; 
                    color: #222; 
                    white-space: pre-wrap;
                }
                
                /* Estilos TipTap */
                .prose {
                    font-family: 'Inter', sans-serif;
                    color: #222;
                    max-width: none;
                }
                .prose p { margin-bottom: 0.5em; }
                .prose ul { list-style-type: disc; padding-left: 1.5em; margin-bottom: 0.5em; }
                .prose ol { list-style-type: decimal; padding-left: 1.5em; margin-bottom: 0.5em; }
                .prose strong { font-weight: bold; }
                .prose em { font-style: italic; }
                .prose a { 
                    color: #0891b2; 
                    text-decoration: underline !important; 
                }
                
                .grid-2 { 
                    display: grid; 
                    grid-template-columns: 1fr 1fr; 
                    gap: 30px; 
                }
                
                .grid-3 {
                    display: grid;
                    grid-template-columns: 1.5fr 1fr 1fr;
                    gap: 30px;
                }
                
                .grid-4 {
                    display: grid;
                    grid-template-columns: 2fr 1fr 1.8fr 1.2fr;
                    gap: 20px;
                }
                
                .report-footer { 
                    margin-top: 60px; 
                    border-top: 3px solid #000; 
                    padding-top: 40px; 
                    font-size: 10pt; 
                    text-align: center; 
                    color: #000; 
                }
                .signature-area {
                    display: flex;
                    justify-content: center;
                    margin-top: 40px;
                    margin-bottom: 60px;
                }
                .signature-box { 
                    text-align: center; 
                    padding: 15px 30px;
                    border: 2px solid #000;
                    position: relative;
                }
                .validation-badge {
                    background: #000;
                    color: #fff;
                    font-size: 7pt;
                    font-weight: 900;
                    padding: 2px 8px;
                    position: absolute;
                    top: -10px;
                    left: 50%;
                    transform: translateX(-50%);
                    white-space: nowrap;
                    letter-spacing: 1px;
                }
                .signature-line { 
                    width: 100%; 
                    margin: 0 auto 5px auto;
                    font-weight: 800; 
                    text-transform: uppercase;
                    font-size: 10pt;
                    color: #000;
                    letter-spacing: 0.5px;
                }
                .signature-subtext {
                    font-size: 9pt;
                    color: #333;
                    font-weight: 600;
                }
                
                .auth-box {
                    margin-top: 40px;
                    padding: 25px;
                    border: 1px solid #e2e8f0;
                    border-left: 10px solid #000;
                    border-radius: 4px;
                    background-color: #f8fafc;
                    text-align: left;
                    page-break-inside: avoid;
                }
                .auth-title {
                    font-weight: 900;
                    text-transform: uppercase;
                    font-size: 9.5pt;
                    margin-bottom: 10px;
                    color: #000;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    letter-spacing: 0.5px;
                }
                .auth-id {
                    font-family: 'JetBrains Mono', 'Courier New', Courier, monospace;
                    font-weight: 700;
                    font-size: 10.5pt;
                    color: #000;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    padding: 6px 12px;
                    display: inline-block;
                    margin-top: 12px;
                    letter-spacing: 1px;
                    border-radius: 4px;
                }
                .links-section { margin-top: 25px; }
                .link-item { 
                    color: #0891b2; 
                    text-decoration: underline !important; 
                    font-weight: 700; 
                    margin-right: 20px; 
                }

                /* Responsividade Mobile */
                @media screen and (max-width: 768px) {
                    .report-container {
                        width: 95%;
                        margin: 20px auto;
                        padding: 10mm 15px;
                        min-height: auto;
                    }
                    .report-header {
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                        gap: 20px;
                    }
                    .report-title-box {
                        text-align: center;
                    }
                    .report-title {
                        font-size: 20pt;
                    }
                    .grid-2, .grid-3, .grid-4 {
                        grid-template-columns: 1fr;
                        gap: 20px;
                    }
                    .conclusion-group .grid-2 {
                        grid-template-columns: 1fr;
                    }
                    .signature-area {
                        margin-top: 30px;
                        margin-bottom: 40px;
                    }
                    .auth-box {
                        padding: 15px;
                    }
                    .auth-id {
                        font-size: 9pt;
                        word-break: break-all;
                    }
                }

                @media print {
                    @page { margin: 0; size: A4; }
                    * { 
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    
                    /* Reset de Layout para Impressão Pública */
                    html, body, #root, .min-h-screen, div[class*="bg-slate-950"], div[class*="bg-slate-950/40"] { 
                        background: white !important; 
                        color: black !important; 
                        margin: 0 !important;
                        padding: 0 !important;
                        height: auto !important;
                        min-height: 0 !important;
                        overflow: visible !important;
                        position: static !important;
                        display: block !important;
                    }
                    
                    .report-container {
                        width: 100% !important;
                        max-width: none !important;
                        margin: 0 !important;
                        padding: 20mm !important;
                        min-height: 100% !important;
                        box-shadow: none !important;
                        display: block !important;
                        background: white !important;
                        border: none !important;
                    }

                    /* Esconder elementos de interface */
                    .fixed, .TechBackground { display: none !important; }

                    /* Evitar quebras no meio de seções */
                    .report-section, .grid-2, .auth-box, .signature-box, .conclusion-group {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }

                    /* Grupo de Conclusão: Forçar início em nova página para centralizar */
                    .conclusion-group {
                        display: block !important;
                        break-before: page;
                        padding-top: 65mm !important; /* Ajuste para efeito de centralização vertical na folha 2 */
                        min-height: 0 !important;
                        margin-top: 0 !important;
                    }
                }

                @media (max-width: 210mm) {
                    .report-container {
                        width: 95%;
                        margin: 20px auto;
                        padding: 10mm;
                    }
                }
                `}
            </style>

            <div className="report-container shadow-2xl">
                <div className="watermark-fixed"></div>
                
                <div className="report-header">
                    <img src="/logo_full.png" alt="Logo" className="header-logo" />
                    <div className="report-title-box">
                        <h1 className="report-title">{report.title}</h1>
                        <p className="report-subtitle">Arcus IT Network Solutions</p>
                    </div>
                </div>

                <div className={report.patrimony || report.hostname ? "grid-4" : "grid-2"}>
                    <div className="report-section">
                        <div className="section-label">Cliente</div>
                        <div className="section-content font-bold" style={{ fontSize: '14pt' }}>{report.clientName}</div>
                    </div>
                    <div className="report-section">
                        <div className="section-label">OS</div>
                        <div className="section-content font-bold" style={{ fontSize: '14pt' }}>#{report.osNumber}</div>
                    </div>
                    {report.hostname && (
                        <div className="report-section">
                            <div className="section-label">Etiqueta | Hostname</div>
                            <div className="section-content font-bold" style={{ fontSize: '14pt' }}>{report.hostname}</div>
                        </div>
                    )}
                    {report.patrimony && (
                        <div className="report-section">
                            <div className="section-label">Patrimônio</div>
                            <div className="section-content font-bold" style={{ fontSize: '14pt' }}>{report.patrimony}</div>
                        </div>
                    )}
                </div>

                <div className="report-section">
                    <div className="section-label">Problema Relatado</div>
                    <div className="section-content prose prose-slate max-w-none" dangerouslySetInnerHTML={renderRichText(report.problemDescription)} />
                </div>

                <div className="report-section">
                    <div className="section-label">Análise Técnica e Constatações</div>
                    <div className="section-content prose prose-slate max-w-none" dangerouslySetInnerHTML={renderRichText(report.technicalAnalysis || report.technicalDiagnosis)} />
                </div>

                <div className="report-section">
                    <div className="section-label">Diagnóstico</div>
                    <div className="section-content prose prose-slate max-w-none" dangerouslySetInnerHTML={renderRichText(report.diagnosis)} />
                </div>

                <div className="conclusion-group">
                    <div className="grid-2">
                        <div className="report-section">
                            <div className="section-label">Custo de Manutenção Estimado</div>
                            <div className="section-content font-black text-2xl">{report.cost || 'R$ 0,00'}</div>
                        </div>
                        <div className="report-section">
                            <div className="section-label">Status / Resposta do Cliente</div>
                            <div className="section-content">{report.clientResponse || 'Pendente'}</div>
                        </div>
                    </div>

                    {(report.referenceLinks && report.referenceLinks.length > 0) && (
                        <div className="report-section links-section">
                            <div className="section-label">Referências de Preços / Justificativa</div>
                            <div className="section-content flex flex-wrap">
                                {report.referenceLinks.map((link, idx) => (
                                    <a key={idx} href={ensureUrl(link.url)} target="_blank" rel="noopener noreferrer" className="link-item">
                                        {link.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="report-footer">
                        <div className="signature-area">
                            <div className="signature-box">
                                <div className="validation-badge">DOCUMENTO VALIDADO</div>
                                <div className="signature-line">{report.createdBy?.split('@')[0].toUpperCase() || 'SISTEMA ARCUS'}</div>
                                <div className="text-[8pt] text-slate-500 font-medium mb-1">{report.createdBy}</div>
                                <div className="signature-subtext">Responsável Técnico Arcus</div>
                            </div>
                        </div>
                        
                        <div className="auth-box">
                            <div className="auth-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                Autenticação do Sistema Arcus
                            </div>
                            <p style={{ margin: 0, color: '#333', fontSize: '9pt', lineHeight: '1.4' }}>
                                Este laudo técnico é um documento original, gerado eletronicamente em <strong>{
                                    report.createdAt 
                                    ? `${report.createdAt.toDate().toLocaleDateString('pt-BR')} às ${report.createdAt.toDate().toLocaleTimeString('pt-BR')}`
                                    : `${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`
                                }</strong>. 
                                A integridade deste relatório é garantida pelo banco de dados central da Arcus Informática sob o identificador único abaixo.
                            </p>
                            <div className="auth-id">ID: {report.id?.toUpperCase()}</div>
                        </div>
                        
                        <p style={{ marginTop: '30px', fontSize: '8pt', color: '#777', textAlign: 'center' }}>
                            Arcus Informática • intranet.arcusinformatica.com.br • {new Date().getFullYear()}
                        </p>
                    </div>
                </div>
                
                {/* Botão de Impressão Flutuante (Opcional para o cliente) */}
                <div className="fixed bottom-8 right-8 z-[100] no-print">
                    <button 
                        onClick={() => window.print()}
                        className="bg-cyan-600 hover:bg-cyan-500 text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95 group"
                        title="Imprimir Laudo"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" /></svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
